import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Crown, ArrowRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", amount: 0 });

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let stop = false;

    const poll = async () => {
      if (stop) return;
      attempts += 1;
      try {
        const r = await api.get(`/stripe/status/${sessionId}`);
        const { payment_status, status, amount } = r.data;
        if (payment_status === "paid") {
          setState({ status: "paid", amount });
          return;
        }
        if (status === "expired") {
          setState({ status: "expired", amount: 0 });
          return;
        }
      } catch (e) {
        // swallow and keep polling
      }
      if (attempts >= MAX_ATTEMPTS) {
        setState({ status: "timeout", amount: 0 });
        return;
      }
      setTimeout(poll, POLL_MS);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="container-narrow py-28 min-h-[70vh]" data-testid="payment-success-page">
      <div className="card-luxe max-w-xl mx-auto text-center">
        {state.status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-[#ECC23D] mx-auto" />
            <h1 className="font-serif text-3xl mt-5">Confirming your payment…</h1>
            <p className="text-white/80 mt-2">Hold tight — this usually takes a few seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 text-[#ECC23D] flex items-center justify-center mx-auto">
              <Check className="w-8 h-8" />
            </div>
            <h1 className="font-serif text-4xl mt-5">Payment received!</h1>
            <p className="text-white/90 mt-3">Thank you — your transaction is confirmed.</p>
            {state.amount ? (
              <p className="font-serif text-3xl text-[#ECC23D] mt-4">${Number(state.amount).toFixed(2)}</p>
            ) : null}
            <p className="text-white/80 text-sm mt-4">A confirmation email is on its way. You can close this page or keep exploring.</p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Link to="/" className="btn-gold">Back Home <ArrowRight className="w-4 h-4"/></Link>
              <Link to="/blueprint" className="btn-ghost-gold"><Crown className="w-4 h-4"/>More Blueprint</Link>
            </div>
          </>
        )}
        {(state.status === "expired" || state.status === "timeout") && (
          <>
            <h1 className="font-serif text-3xl mt-2">We couldn't confirm the payment</h1>
            <p className="text-white/80 mt-3">
              {state.status === "expired"
                ? "Your checkout session expired before payment was completed."
                : "It's taking longer than expected. If you were charged, check your email — otherwise please try again."}
            </p>
            <Link to="/blueprint" className="btn-fuchsia mt-6 inline-flex">Try again</Link>
          </>
        )}
      </div>
    </div>
  );
}
